/**
 * Экран справочников Экономиста — в одной манере с «Техникой» и «Ставками
 * групп»: панель инструментов сверху, таблица записей, запись — в боковой
 * панели. Ширина реагирует на контейнер (`handbooks`), а не на окно.
 */
import styled from 'styled-components';
import { Control, fleetLeading, fleetType } from '@entities/fleet';
import { riseIn } from '@shared/theme/animations';

/** Узкий контейнер: панель инструментов в колонку, таблица — с прокруткой. */
export const NARROW = '640px';

export const Root = styled.div`
  container: handbooks / inline-size;
  animation: ${riseIn} 320ms ${({ theme }) => theme.motion.easeOut} both;
`;

export const Lead = styled.p`
  max-width: 880px;
  margin: ${({ theme }) => `${theme.spacing(4)} 0 0`};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

export const PanelBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};
`;

/** Поиск, фильтр и действия над списком — над таблицей, а не под ней. */
export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  font-size: ${fleetType.body};

  & > .search {
    flex: 0 1 320px;
    min-width: 200px;
  }

  & > .count {
    margin: 0;
    font-size: ${fleetType.body};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  & > .spacer {
    flex: 1 1 auto;
  }

  @container handbooks (max-width: ${NARROW}) {
    & > .search,
    & > button {
      flex: 1 1 100%;
    }

    & > .spacer {
      display: none;
    }
  }
`;

export const TableBox = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

/** На узком экране таблица не сжимается в столбики по слову — прокручивается
 *  вбок внутри рамки. */
export const Table = styled.table`
  width: 100%;
  min-width: 640px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  thead th {
    position: sticky;
    top: 0;
    padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    text-align: left;
    white-space: nowrap;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  th.num,
  td.num {
    text-align: right;
  }

  td {
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
    color: ${({ theme }) => theme.colors.text.primary};
    transition: background ${({ theme }) => theme.motion.fast}
      ${({ theme }) => theme.motion.easeOut};
  }

  td.num {
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  td.status {
    width: 1%;
    white-space: nowrap;
  }

  /* Ширина колонок по смыслу: длинная строка БДР не сжимается в столбик по
     слову, короткий признак — по содержимому. */
  th.wide,
  td.wide {
    min-width: 260px;
    width: 32%;
  }

  th.mid,
  td.mid {
    min-width: 180px;
  }

  th.fit,
  td.fit {
    width: 1%;
    white-space: nowrap;
  }

  td .sub {
    margin-top: ${({ theme }) => theme.spacing(0.5)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    overflow-wrap: anywhere;
  }

  tbody tr:last-child > td {
    border-bottom: none;
  }

  tbody tr[data-open='true'] {
    cursor: pointer;
  }

  tbody tr:hover > td,
  tbody tr:focus-within > td {
    background: ${({ theme }) => theme.colors.surface};
  }

  tbody tr[data-inactive='true'] > td {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

/** Таблица долей: в ячейках поля ввода, поэтому всё по центру строки. */
export const SplitTable = styled(Table)`
  min-width: 480px;

  td {
    vertical-align: middle;
  }
`;

/** Ключ записи — кнопка: с клавиатуры запись открывается так же, как щелчком.
 *  Номер лицензии и код не переносятся по словам; длинное название
 *  (`$wrap`) — переносится, иначе распирает таблицу. */
export const KeyButton = styled.button<{ $wrap?: boolean }>`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  text-align: left;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: ${({ $wrap }) => ($wrap ? 'normal' : 'nowrap')};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus-visible {
    outline: none;
    border-radius: ${({ theme }) => theme.radius.xs};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/** Пустой справочник или ничего не нашлось: что здесь будет и что сделать. */
export const EmptyCard = styled.div`
  max-width: 680px;
  padding: ${({ theme }) => theme.spacing(6)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.lg};

  & > h3 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.lead};
    font-weight: 600;
    text-wrap: balance;
  }

  & > p {
    margin: 0;
    font-size: ${fleetType.base};
    line-height: ${fleetLeading.normal};
    color: ${({ theme }) => theme.colors.text.secondary};
    text-wrap: pretty;
  }

  & > .actions {
    display: flex;
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing(2)};
    margin-top: ${({ theme }) => theme.spacing(4)};
  }
`;

export const FlashBox = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

/** Правило классификации — JSON моноширинным шрифтом. */
export const RuleBox = styled.details`
  margin-top: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};

  & > summary {
    padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
    font-size: ${fleetType.body};
    font-weight: 600;
    cursor: pointer;
  }

  & > .body {
    padding: ${({ theme }) => `0 ${theme.spacing(4)} ${theme.spacing(4)}`};
  }

  & textarea {
    min-height: 110px;
    font-family: ${({ theme }) => theme.font.mono};
    font-size: ${fleetType.small};
  }
`;

/** Сумма долей распределения прибыли: зелёная — ровно 100 %. */
export const SumLine = styled.p<{ $ok: boolean }>`
  margin: 0;
  font-size: ${fleetType.body};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ $ok, theme }) => ($ok ? theme.colors.status.success : theme.colors.status.warning)};
`;

/** Месяц распределения — шириной с дату, а не во всю строку. */
export const PeriodControl = styled(Control)`
  width: 200px;
`;

/** Доля в процентах: число вправо, как в колонке. */
export const PctControl = styled(Control)`
  width: 140px;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

/** Строка «добавить объект · сумма · сохранить» под таблицей долей. */
export const SumBar = styled(Toolbar)`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;
